/**
 * Web Push subscription helpers for the ASAP React app.
 *
 * - registerPushServiceWorker(): registers /asap-sw.js on the origin root scope.
 * - isPushSupported(): true if the browser exposes ServiceWorker + Notification + PushManager.
 * - subscribeToPush(): asks permission (if needed) and stores the subscription on the backend.
 * - unsubscribeFromPush(): removes the subscription locally and on the backend.
 */
import api from './api';

const PUBLIC_KEY = (process.env.REACT_APP_VAPID_PUBLIC_KEY || '').trim();
const SW_URL = '/asap-sw.js';

export function isPushSupported() {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'Notification' in window &&
    'PushManager' in window
  );
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

let _registration = null;
export async function registerPushServiceWorker() {
  if (!isPushSupported()) return null;
  if (_registration) return _registration;
  try {
    _registration = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
    return _registration;
  } catch (e) {
    console.error('[asap] SW register failed', e);
    return null;
  }
}

export async function getExistingSubscription() {
  if (!isPushSupported()) return null;
  const reg = await registerPushServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (!PUBLIC_KEY) return { ok: false, reason: 'no-vapid-key' };

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  const reg = await registerPushServiceWorker();
  if (!reg) return { ok: false, reason: 'sw-failed' };

  // Wait for service worker to become active before subscribing.
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY),
    });
  }

  const json = sub.toJSON();
  await api.post('/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    user_agent: (navigator.userAgent || '').slice(0, 200),
  });

  try { window.localStorage.setItem('asap_push_state', 'subscribed'); } catch {/* ignore */}
  return { ok: true };
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch {/* ignore */}
  try { await api.post('/push/unsubscribe', { endpoint }); } catch {/* ignore */}
  try { window.localStorage.setItem('asap_push_state', 'unsubscribed'); } catch {/* ignore */}
  return { ok: true };
}

/** Listen for service-worker click navigation messages. */
export function attachPushClickHandler(navigate) {
  if (!('serviceWorker' in navigator)) return () => {};
  const handler = (event) => {
    if (event.data && event.data.type === 'asap-push-navigate' && event.data.url) {
      try { navigate(event.data.url); } catch {/* ignore */}
    }
  };
  navigator.serviceWorker.addEventListener('message', handler);
  return () => navigator.serviceWorker.removeEventListener('message', handler);
}
